import { useNodeId } from "@xyflow/react";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import { Suspense, useCallback, useEffect, useState, useRef } from "react";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  createNewInterfaceAtom_SUSPENDS,
  interfacesAtom_SUSPENDS,
  interfacesUsageAtom_SUSPENDS,
  shouldCreateNewInterfaceAtom,
  updateNodeSetter,
  saveCanvasSetter,
  formsTerminologyAtom,
  moveAssetToCanvasFolderSetter,
} from "@zapier/canvas-state";

import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { CreateAssetLoadingButton } from "../CreateAssetLoadingButton";
import { ErrorBoundary } from "../ErrorBoundary";
import { UpsellPopover } from "../UpsellPopover";

function SuspendedCreateNewInterfaceButton() {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);
  const nodeId = useNodeId();
  const setShouldCreateNewInterface = useSetAtom(shouldCreateNewInterfaceAtom);
  const createNewInterfaceResponse = useAtomValue(
    createNewInterfaceAtom_SUSPENDS,
  );
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const { emitEventOnInterfaceCreation } = useEmitResourceCreatedEvents();
  const refreshInterfacesProjects = useSetAtom(interfacesAtom_SUSPENDS);
  const moveAssetToFolder = useSetAtom(moveAssetToCanvasFolderSetter);

  const [interfacesUsage, refreshInterfacesUsage] = useAtom(
    interfacesUsageAtom_SUSPENDS,
  );

  // Popover state
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createNewInterface = useCallback(() => {
    // If upgrade is needed, show popover instead of creating interface
    if (interfacesUsage?.needsToUpgrade) {
      setIsPopoverOpen(true);
      return;
    }

    setShouldCreateNewInterface(true);
  }, [setShouldCreateNewInterface, interfacesUsage?.needsToUpgrade]);

  // Reset the shouldCreateNewInterfaceAtom to false after the createNewInterfaceResponse completes
  useEffect(() => {
    if (createNewInterfaceResponse !== null) {
      const pagesKeys = Object.keys(createNewInterfaceResponse.pages || {});
      const pageId = pagesKeys.length > 0 ? pagesKeys[0] : undefined;
      const projectId = createNewInterfaceResponse.id;

      if (projectId) {
        const updatedNodes = updateNode({
          id: nodeId!,
          data: {
            projectId,
            pageId,
          },
        });
        saveToBackend({ nodes: updatedNodes });
        refreshInterfacesProjects(); // Trigger interfaces projects to be re-fetched, so the new interface can be populated in the InterfacesProjects Typeahead
        refreshInterfacesUsage(); // Because projectCount would've changed
        setShouldCreateNewInterface(false);

        emitEventOnInterfaceCreation(projectId);
        moveAssetToFolder({ assetType: "interface", assetId: projectId });
      }
    }
  }, [
    refreshInterfacesUsage,
    createNewInterfaceResponse,
    setShouldCreateNewInterface,
    saveToBackend,
    updateNode,
    emitEventOnInterfaceCreation,
    refreshInterfacesProjects,
    moveAssetToFolder,
    nodeId,
  ]);

  return (
    <>
      <Button
        // @ts-ignore
        ref={buttonRef}
        variant={interfacesUsage?.needsToUpgrade ? "tertiary" : "primary"}
        size="compact"
        iconPosition="start"
        onPress={createNewInterface}
      >
        <Icon name="formAdd" />
        New {interfacesTerms.singular}
      </Button>
      {interfacesUsage?.needsToUpgrade && (
        <UpsellPopover
          assetType="interface"
          triggerRef={buttonRef}
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
        />
      )}
    </>
  );
}

export function CreateNewInterfaceButton() {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <CreateAssetLoadingButton>
            New {interfacesTerms.singular}
          </CreateAssetLoadingButton>
        }
      >
        <SuspendedCreateNewInterfaceButton />
      </Suspense>
    </ErrorBoundary>
  );
}
